import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { money, titleCase, WATCH_STATUSES, STATUS_COLORS, can, MANAGE_WATCHES } from "@/lib/format";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Star, Watch as WatchIcon } from "lucide-react";

export default function AdminWatches() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const editable = can(user?.role, MANAGE_WATCHES);
  const [watches, setWatches] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    try {
      const r = await api.get("/admin/watches", { params });
      setWatches(r.data);
    } catch {
      toast.error("Failed to load watches");
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const setStatusOf = (w, s) =>
    act(() => api.patch(`/admin/watches/${w.id}/status`, { status: s }), `Marked ${titleCase(s)}`);
  const setAuthOf = (w, s) =>
    act(() => api.patch(`/admin/watches/${w.id}/authentication`, { authentication_status: s }), `Authentication: ${titleCase(s)}`);
  const toggleTopPick = (w) =>
    act(() => api.patch(`/admin/watches/${w.id}/top-pick`, { top_pick: !w.top_pick }), w.top_pick ? "Removed Top Pick" : "Marked Top Pick");
  const requestDeposit = (w) =>
    act(() => api.post(`/admin/watches/${w.id}/request-deposit`), "Deposit requested (task + notification created)");
  const genDossier = (w) =>
    act(() => api.post(`/admin/watches/${w.id}/generate-dossier`), "Dossier generated");
  const doDelete = () =>
    act(() => api.delete(`/admin/watches/${toDelete.id}`), "Watch deleted").then(() => setToDelete(null));

  return (
    <div data-testid="admin-watches">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold">Watches</h1>
          <p className="text-sm text-[var(--text-2)]">{watches.length} in inventory</p>
        </div>
        {editable && (
          <Button onClick={() => navigate("/admin/watches/new")} data-testid="add-watch-btn"
            className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
            <Plus size={16} className="mr-2" /> Add Watch
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input placeholder="Search brand, model, reference…" value={q}
          onChange={(e) => setQ(e.target.value)} className="sm:max-w-xs bg-[var(--panel)]" data-testid="watch-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-56 bg-[var(--panel)]" data-testid="watch-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {WATCH_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-[var(--border-lch)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Watch</th>
                <th className="text-left p-3">Reference</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Auth</th>
                <th className="text-left p-3">Price</th>
                <th className="text-left p-3">Top Pick</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {watches.map((w) => (
                <tr key={w.id} className="border-t border-[var(--border-lch)] hover:bg-[var(--panel-2)]/50" data-testid={`watch-row-${w.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded bg-[var(--panel-2)] overflow-hidden flex items-center justify-center shrink-0">
                        {w.public_photos?.[0]
                          ? <img src={fileUrl(w.public_photos[0].url)} alt="" className="h-full w-full object-cover" />
                          : <WatchIcon size={18} className="text-[var(--muted)]" />}
                      </div>
                      <div>
                        <button onClick={() => editable ? navigate(`/admin/watches/${w.id}`) : null}
                          className="font-medium text-left hover:text-[#9a7b3f]">{w.brand} {w.model}</button>
                        <div className="text-xs text-[var(--muted)]">{w.year || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[var(--text-2)]">{w.reference_number || "—"}</td>
                  <td className="p-3"><Badge variant="outline" className={STATUS_COLORS[w.status]}>{titleCase(w.status)}</Badge></td>
                  <td className="p-3"><Badge variant="outline" className={STATUS_COLORS[w.authentication_status]}>{titleCase(w.authentication_status)}</Badge></td>
                  <td className="p-3 text-[var(--text)]">{w.price_on_request ? <span className="italic text-[var(--text-2)]">On Request</span> : (money(w.price) || "—")}</td>
                  <td className="p-3">{w.top_pick ? <Star size={16} className="text-[#c9a86a] fill-[#c9a86a]" /> : <span className="text-[var(--muted)]">—</span>}</td>
                  <td className="p-3 text-right">
                    {editable ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`watch-actions-${w.id}`}><MoreHorizontal size={18} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onClick={() => navigate(`/admin/watches/${w.id}`)}>Edit Watch</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleTopPick(w)}>{w.top_pick ? "Remove Top Pick" : "Mark as Top Pick"}</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs">Set Status</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => setStatusOf(w, "available")}>Mark Available</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatusOf(w, "reserved")}>Mark Reserved</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatusOf(w, "sold")}>Mark Sold</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatusOf(w, "private_drop")}>Private Drop</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatusOf(w, "hidden")}>Hide</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs">Authentication</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => setAuthOf(w, "pending")}>Send to Authentication</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setAuthOf(w, "passed")}>Mark Auth Passed</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setAuthOf(w, "failed")}>Mark Auth Failed</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => requestDeposit(w)}>Request Deposit</DropdownMenuItem>
                          {w.dossier_id ? (
                            <DropdownMenuItem onClick={() => navigate(`/admin/dossiers?open=${w.dossier_id}`)} data-testid={`view-dossier-${w.id}`}>View Dossier</DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => genDossier(w)} data-testid={`gen-dossier-${w.id}`}>Generate Dossier</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600" onClick={() => setToDelete(w)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : <span className="text-xs text-[var(--muted)]">View only</span>}
                  </td>
                </tr>
              ))}
              {!loading && !watches.length && (
                <tr><td colSpan={7} className="p-8 text-center text-[var(--muted)]">No watches found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this watch?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.brand} {toDelete?.model} will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
